import { getDataSourceApiPerRegion } from 'common/api'

export type CustomerAttributesResponse = {
  name: string
  id: string
  description: string
  isUsed: boolean
  isCmabUsed: boolean
}

export const updateCustomerAttributes = async ({
  accountId,
  attributeId,
  catalogueAttributeId,
  region,
  description,
  isUsed,
  isCmabUsed,
}: {
  accountId: string
  region: string
  catalogueAttributeId: string
  attributeId: string
  description: string
  isUsed: boolean
  isCmabUsed: boolean
}) => {
  const result = await getDataSourceApiPerRegion(region).patch<{
    items: ProfileAttributeItemResponse[]
  }>(
    `accounts/${accountId}/profile-attributes-catalogues/${catalogueAttributeId}/attributes/${attributeId}`,
    { description, include_in_gen: isUsed, include_in_cmab: isCmabUsed }
  )
  return result.data.items
}

export type ProductCatalogCategoriesResponse = {
  product_categories: Object
  raw_data: {
    id: string
    name: string | null
    description: string | null
    ccn_path: string[]
  }[]
}

export const getProductCatalogCategories = async ({
  accountId,
  catalogId,
  region,
}: {
  accountId: string
  catalogId: string
  region: string
}): Promise<ProductCatalogCategoriesResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<ProductCatalogCategoriesResponse>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories`
  )
  return result.data
}

export type ProductCatalogSettingsResponse = {
  id: string
  name: string
  account_id: string
  created_at: string
  last_updated: string
  mapping: {
    import_type: string
    fields: {
      destination_field_name: string
      required: boolean
      source_field_name: string
      product_category_level?: number
      product_category_delimiter?: ';' | '>'
      category_description_level?: number
    }[]
  }
}

export const getProductCatalogSettings = async ({
  accountId,
  productCatalogId,
  region,
}: {
  accountId: string
  productCatalogId: string
  region: string
}): Promise<ProductCatalogSettingsResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<ProductCatalogSettingsResponse>(
    `accounts/${accountId}/product-catalogues/${productCatalogId}`
  )
  return result.data
}

type ProfileAttributesCatalogueResponse = {
  items: {
    account_id: string
    created_at: string
    description: string
    id: string
    name: string
    updated_at: null
  }[]
}

export interface ProfileAttribute {
  id: string
  name: string
  region: string
  updated: string
}

export const getCustomerAttributeRecords = async ({
  accountId,
  privacyRegions,
}: {
  accountId: string
  privacyRegions: string[]
}): Promise<ProfileAttribute[]> => {
  if (!privacyRegions || privacyRegions.length === 0) {
    throw new Error(
      "We're missing your region info for data privacy. Please contact support to resolve this. Thank you!"
    )
  }

  const results = await Promise.all(
    privacyRegions.map((region) =>
      getDataSourceApiPerRegion(region)
        .get<ProfileAttributesCatalogueResponse>(
          `accounts/${accountId}/profile-attributes-catalogues`
        )
        .then((response) => ({
          region,
          items: response.data.items,
        }))
    )
  )
  return results
    .map(({ region, items }) =>
      items.map(({ id, name, updated_at, created_at }) => ({
        id,
        name,
        region,
        updated: updated_at ?? created_at,
      }))
    )
    .flat()
}

export interface ProfileAttributeItemResponse {
  id: string
  profile_attribute_catalogue_id: string
  name: string
  description: string
  isUsed: boolean
  created_at: string
  updated_at: string
  include_in_gen: boolean | undefined
  include_in_cmab: boolean | undefined
}

export const getCustomerAttributes = async ({
  accountId,
  catalogueAttributeId,
  region,
}: {
  accountId: string
  catalogueAttributeId: string
  region: string
}) => {
  const result = await getDataSourceApiPerRegion(region).get<{
    items: ProfileAttributeItemResponse[]
  }>(
    `accounts/${accountId}/profile-attributes-catalogues/${catalogueAttributeId}/attributes`
  )
  return result.data.items.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    isUsed: item.include_in_gen ?? false,
    isCmabUsed: item.include_in_cmab ?? false,
  }))
}

export const createProfileAttributesCatalogue = async ({
  accountId,
  region,
  name,
}: {
  accountId: string
  region: string
  name: string
}): Promise<{ id: string; name: string; description: string }> => {
  const result = await getDataSourceApiPerRegion(region).post<{
    id: string
    name: string
    description: string
  }>(`accounts/${accountId}/profile-attributes-catalogues`, { name })
  return result.data
}

export type Product = {
  id: string
  name: string
  description: string
  ccn_path: string[]
  category_id: string
}

export type CategoryProductsResponse = {
  data: Product[]
  total: number
  currentPage: number
  perPage: number
  totalItems: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export type CategoryDetailsResponse = {
  id: string
  name: string | null
  description: string | null
  cc_name: string
  ccn_path: string[]
}

export const getCategoryDetails = async ({
  accountId,
  catalogId,
  ccnPath,
  region,
}: {
  accountId: string
  catalogId: string
  ccnPath: string
  region: string
}): Promise<CategoryDetailsResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<CategoryDetailsResponse>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories`,
    { params: { ccn_path: ccnPath } }
  )
  return result.data
}

export const getCategoryProducts = async ({
  accountId,
  catalogId,
  categoryId,
  region,
  page,
  limit,
}: {
  accountId: string
  catalogId: string
  categoryId: string
  region: string
  page: number
  limit: number
}): Promise<CategoryProductsResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<CategoryProductsResponse>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories/${categoryId}/products`,
    { params: { limit, page } }
  )
  return result.data
}

export type ProductCatalogSearchQueryVariables = {
  accountId: string
  catalogId: string
  region: string
  query: string
  page: number
  pageSize: number
}

export type ProductCatalogSearchResultItem = {
  id: string
  name: string
  description: string
  category_id: string
  ccn_path: string[]
}

export type PaginatedProductCatalogSearchResults = {
  data: ProductCatalogSearchResultItem[]

  total: number
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  perPage: number
  totalPages: number
}

export const searchProductCatalog = async ({
  accountId,
  catalogId,
  region,
  query,
  page,
  pageSize,
}: ProductCatalogSearchQueryVariables): Promise<PaginatedProductCatalogSearchResults> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<PaginatedProductCatalogSearchResults>(
    `accounts/${accountId}/product-catalogues/${catalogId}/products/search`,
    { params: { search: query, limit: pageSize, page } }
  )
  return result.data
}

export type ProductDetailsResponse = {
  category_id: string
  ccn_path: string[]
  description: string
  id: string
  name: string
}

export const getProductDetails = async ({
  accountId,
  catalogId,
  categoryId,
  productId,
  region,
}: {
  accountId: string
  catalogId: string
  categoryId: string
  productId: string
  region: string
}): Promise<ProductDetailsResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.get<ProductDetailsResponse>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories/${categoryId}/products/${productId}`
  )
  return result.data
}

export const updateCategoryDescription = async ({
  accountId,
  catalogId,
  categoryId,
  region,
  description,
}: {
  accountId: string
  catalogId: string
  categoryId: string
  region: string
  description: string
}): Promise<CategoryDetailsResponse> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.patch<CategoryDetailsResponse>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories/${categoryId}`,
    { description }
  )
  return result.data
}

export const updateProductDescription = async ({
  accountId,
  catalogId,
  categoryId,
  productId,
  region,
  description,
}: {
  accountId: string
  catalogId: string
  categoryId: string
  productId: string
  region: string
  description: string
}): Promise<Product> => {
  const api = getDataSourceApiPerRegion(region)
  const result = await api.patch<Product>(
    `accounts/${accountId}/product-catalogues/${catalogId}/categories/${categoryId}/products/${productId}`,
    { description }
  )
  return result.data
}

export const deleteCustomerAttributeRecord = async ({
  accountId,
  catalogueAttributeId,
  region,
}: {
  accountId: string
  catalogueAttributeId: string
  region: string
}): Promise<void> => {
  const api = getDataSourceApiPerRegion(region)
  await api.delete<void>(
    `accounts/${accountId}/profile-attributes-catalogues/${catalogueAttributeId}`
  )
}

export const deleteProductCatalog = async ({
  accountId,
  productCatalogId,
  region,
}: {
  accountId: string
  productCatalogId: string
  region: string
}): Promise<void> => {
  const api = getDataSourceApiPerRegion(region)
  await api.delete<void>(
    `accounts/${accountId}/product-catalogues/${productCatalogId}`
  )
}
